import { useCallback, useEffect, useState } from 'react'
import { PREFERENCE, readPreference, writePreference } from './prefs'

/**
 * Alternative colours for people who need them: high contrast for low vision,
 * soft for sensitivity to glare. Each works with the light and dark themes.
 */
export type ContrastChoice = 'system' | 'standard' | 'high' | 'soft'
const CHOICES: readonly string[] = ['system', 'standard', 'high', 'soft']

/** The contrast actually shown: 'system' follows the system's "more contrast" setting. */
function resolvedContrast(choice: ContrastChoice): Exclude<ContrastChoice, 'system'> {
  if (choice !== 'system') return choice
  return typeof matchMedia === 'function' && matchMedia('(prefers-contrast: more)').matches
    ? 'high'
    : 'standard'
}

function apply(choice: ContrastChoice): void {
  const shown = resolvedContrast(choice)
  if (shown === 'standard') delete document.documentElement.dataset.contrast
  else document.documentElement.dataset.contrast = shown
}

/** Follows the system by default; an explicit choice is remembered. */
export function useContrast(): [ContrastChoice, (next: ContrastChoice) => void] {
  const [choice, setChoice] = useState<ContrastChoice>(() => {
    const stored = readPreference(PREFERENCE.contrast)
    return stored && CHOICES.includes(stored) ? (stored as ContrastChoice) : 'system'
  })

  useEffect(() => {
    apply(choice)
    if (choice !== 'system' || typeof matchMedia !== 'function') return
    const media = matchMedia('(prefers-contrast: more)')
    const onChange = () => apply('system')
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [choice])

  const set = useCallback((next: ContrastChoice) => {
    setChoice(next)
    writePreference(PREFERENCE.contrast, next)
  }, [])

  return [choice, set]
}
